// Shared Project + Budget pickers for transaction documents.
// All Sales Orders / Invoices / POs / Bills MUST link to a project + budget.
import React, { useEffect, useState } from 'react';
import fb from '../api';
import { Select } from '../ui';

/**
 * Pair of <Select> controls for project_id + budget_id.
 *
 * Props:
 *   value      { project_id, budget_id }
 *   onChange   ({ project_id, budget_id }) => void
 *   kind       'sales' | 'purchase'
 *              - sales   → only Execution budgets of the chosen project
 *              - purchase → Execution (of project) + global Overhead pool
 *   required   default true
 *   testidPrefix string used for data-testid
 */
export default function ProjectBudgetPicker({ value = {}, onChange, kind = 'sales',
                                              required = true, testidPrefix = 'doc' }) {
  const [projects, setProjects] = useState([]);
  const [budgets, setBudgets] = useState([]);

  useEffect(() => { (async () => setProjects(await fb.get('/projects')))(); }, []);

  useEffect(() => {
    (async () => {
      if (!value.project_id) { setBudgets([]); return; }
      const projBuds = await fb.get('/budgets', { project_id: value.project_id });
      const execOnly = projBuds.filter((b) => b.kind === 'execution');
      if (kind === 'sales') return setBudgets(execOnly);
      // purchases can hit project-exec OR global overhead pool
      const overhead = await fb.get('/budgets', { standalone: true, kind: 'overhead' });
      setBudgets([...execOnly, ...overhead]);
    })();
  }, [value.project_id, kind]);

  const handleProj = (pid) => onChange({ project_id: pid || '', budget_id: '' });
  const handleBud = (bid) => onChange({ ...value, budget_id: bid || '' });

  return (
    <>
      <Select label={`Project ${required ? '*' : ''}`} value={value.project_id || ''}
        onChange={(e) => handleProj(e.target.value)} data-testid={`${testidPrefix}-project`}>
        <option value="">— Select Project —</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
      </Select>
      <Select label={`Budget ${required ? '*' : ''}`} value={value.budget_id || ''}
        onChange={(e) => handleBud(e.target.value)} disabled={!value.project_id}
        data-testid={`${testidPrefix}-budget`}>
        <option value="">{!value.project_id ? '— Pick project first —' : '— Select Budget —'}</option>
        {budgets.map((b) => (
          <option key={b.id} value={b.id}>
            {b.kind === 'execution' ? '[E]' : '[O]'} {b.name} {b.project_name ? `· ${b.project_name}` : '· Overhead pool'}
          </option>
        ))}
      </Select>
    </>
  );
}
